import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function Loans() {
  const [loans, setLoans] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showSimulator, setShowSimulator] = useState(false);
  const [simulation, setSimulation] = useState(null);
  const [formData, setFormData] = useState({
    account_id: '',
    loan_type: 'personal',
    amount_requested: '',
    duration_months: '12',
    description: ''
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [loansRes, accountsRes] = await Promise.all([
        axios.get(`${API_URL}/loans`, config),
        axios.get(`${API_URL}/accounts`, config)
      ]);
      setLoans(loansRes.data);
      // Filtrer uniquement les comptes actifs
      setAccounts(accountsRes.data.filter(acc => acc.status === 'active'));
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Vérifier si l'utilisateur a au moins un compte actif
  const hasActiveAccount = accounts.length > 0;

  const calculateLoan = () => {
    const amount = parseFloat(formData.amount_requested);
    const months = parseInt(formData.duration_months);
    const rate = loanTypes[formData.loan_type]?.rate || 0;
    
    if (!amount || !months) {
      alert('Veuillez remplir le montant et la durée');
      return;
    }
    
    // Calculate monthly payment using amortization formula
    const monthlyRate = rate / 100 / 12;
    const monthlyPayment = monthlyRate === 0 
      ? amount / months 
      : (amount * monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
    
    const totalAmount = monthlyPayment * months;
    const totalInterest = totalAmount - amount;
    
    setSimulation({
      amount,
      months,
      rate,
      monthlyPayment: monthlyPayment.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
      totalInterest: totalInterest.toFixed(2),
      loanType: loanTypes[formData.loan_type]?.name
    });
    setShowSimulator(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Force simulation before submission
    if (!simulation) {
      alert('Veuillez d\'abord simuler votre crédit avant de soumettre la demande');
      calculateLoan();
      return;
    }
    
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API_URL}/loans`, {
        ...formData,
        amount_requested: parseFloat(formData.amount_requested),
        duration_months: parseInt(formData.duration_months)
      }, config);
      alert('Demande de crédit envoyée avec succès !');
      setShowForm(false);
      setShowSimulator(false);
      setSimulation(null);
      setFormData({ account_id: '', loan_type: 'personal', amount_requested: '', duration_months: '12', description: '' });
      loadData();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  const loanTypes = {
    personal: { name: 'Crédit Personnel', icon: '👤', rate: 4.5, description: 'Pour vos projets personnels' },
    mortgage: { name: 'Crédit Immobilier', icon: '🏠', rate: 2.8, description: 'Achat de votre résidence' },
    auto: { name: 'Crédit Auto', icon: '🚗', rate: 3.5, description: 'Financement véhicule' },
    business: { name: 'Crédit Professionnel', icon: '🏢', rate: 5.2, description: 'Pour votre entreprise' },
    student: { name: 'Crédit Étudiant', icon: '🎓', rate: 1.5, description: 'Financement études' },
    consolidation: { name: 'Rachat de Crédit', icon: '🔄', rate: 3.9, description: 'Regroupez vos crédits' }
  };

  if (loading) {
    return <div className="text-center py-20">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Mes Crédits</h1>
        {hasActiveAccount ? (
          <button
            onClick={() => {
              setShowForm(!showForm);
              if (showForm) {
                setShowSimulator(false);
                setSimulation(null);
              }
            }}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            {showForm ? 'Annuler' : 'Demander un crédit'}
          </button>
        ) : (
          <div className="px-6 py-3 bg-gray-300 text-gray-600 rounded-lg font-semibold cursor-not-allowed" title="Vous devez avoir un compte actif">
            🔒 Compte actif requis
          </div>
        )}
      </div>

      {/* Message si pas de compte actif */}
      {!hasActiveAccount && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="text-4xl">⚠️</div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-red-900 mb-2">Compte actif requis</h3>
              <p className="text-red-700 mb-3">
                Vous devez être client de la <strong>Banque Kreiz Breizh</strong> avec un compte actif pour pouvoir demander un crédit.
              </p>
              <div className="bg-white rounded-lg p-4 border border-red-200">
                <p className="text-sm text-gray-700 mb-2"><strong>Pour demander un crédit :</strong></p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>✓ Ouvrez un compte bancaire auprès de la BKB</li>
                  <li>✓ Vérifiez votre identité (KYC)</li>
                  <li>✓ Attendez l'activation de votre compte</li>
                  <li>✓ Revenez ensuite sur cette page</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loan Types Overview */}
      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
        {Object.entries(loanTypes).map(([key, type]) => (
          <div key={key} className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white text-center">
            <div className="text-4xl mb-2">{type.icon}</div>
            <h3 className="font-bold text-sm mb-1">{type.name}</h3>
            <p className="text-xl font-bold">{type.rate}%</p>
            <p className="text-xs text-blue-100 mt-1">{type.description}</p>
          </div>
        ))}
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Demande de crédit</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Compte de versement *
              </label>
              <select
                value={formData.account_id}
                onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Sélectionner un compte</option>
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.account_type} - {acc.iban} ({acc.balance.toFixed(2)} €)
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de crédit *
              </label>
              <select
                value={formData.loan_type}
                onChange={(e) => setFormData({ ...formData, loan_type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {Object.entries(loanTypes).map(([key, type]) => (
                  <option key={key} value={key}>
                    {type.icon} {type.name} - Taux: {type.rate}%
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Montant demandé (€) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount_requested}
                  onChange={(e) => setFormData({ ...formData, amount_requested: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Durée (mois) *
                </label>
                <select
                  value={formData.duration_months}
                  onChange={(e) => setFormData({ ...formData, duration_months: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="12">12 mois (1 an)</option>
                  <option value="24">24 mois (2 ans)</option>
                  <option value="36">36 mois (3 ans)</option>
                  <option value="60">60 mois (5 ans)</option>
                  <option value="84">84 mois (7 ans)</option>
                  <option value="120">120 mois (10 ans)</option>
                  <option value="180">180 mois (15 ans)</option>
                  <option value="240">240 mois (20 ans)</option>
                  <option value="300">300 mois (25 ans)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motif du crédit *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Décrivez l'utilisation prévue du crédit..."
                required
              />
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-900 font-semibold mb-2">📋 Informations importantes</p>
              <ul className="text-xs text-blue-700 space-y-1">
                <li>• Taux fixe garanti sur toute la durée</li>
                <li>• Réponse sous 48h ouvrées</li>
                <li>• Aucun frais de dossier</li>
                <li>• Remboursement anticipé sans pénalité</li>
              </ul>
            </div>

            {/* Simulation Button */}
            <button
              type="button"
              onClick={calculateLoan}
              className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              🧮 Simuler mon crédit
            </button>

            {/* Simulation Results */}
            {showSimulator && simulation && (
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 border-2 border-purple-300">
                <h3 className="text-xl font-bold text-purple-900 mb-4 flex items-center gap-2">
                  <span>📊</span> Simulation de votre crédit
                </h3>
                
                <div className="bg-white rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <p className="text-xs text-gray-600">Type de crédit</p>
                      <p className="font-bold text-gray-900">{simulation.loanType}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Taux d'intérêt</p>
                      <p className="font-bold text-blue-600">{simulation.rate}%</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-600">Montant emprunté</p>
                      <p className="font-bold text-gray-900">{parseFloat(simulation.amount).toLocaleString('fr-FR')} €</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Durée</p>
                      <p className="font-bold text-gray-900">{simulation.months} mois</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-4 mb-4 border-2 border-green-300">
                  <p className="text-sm text-green-800 mb-1">Mensualité</p>
                  <p className="text-3xl font-bold text-green-900">{parseFloat(simulation.monthlyPayment).toLocaleString('fr-FR')} €</p>
                  <p className="text-xs text-green-700 mt-1">à payer chaque mois</p>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="text-xs text-gray-600">Coût total du crédit</p>
                    <p className="text-lg font-bold text-orange-600">{parseFloat(simulation.totalInterest).toLocaleString('fr-FR')} €</p>
                    <p className="text-xs text-gray-500">Intérêts</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="text-xs text-gray-600">Montant total à rembourser</p>
                    <p className="text-lg font-bold text-gray-900">{parseFloat(simulation.totalAmount).toLocaleString('fr-FR')} €</p>
                    <p className="text-xs text-gray-500">Capital + Intérêts</p>
                  </div>
                </div>

                <div className="bg-blue-600 text-white rounded-lg p-3 text-center">
                  <p className="text-sm font-semibold">✓ Simulation validée - Vous pouvez maintenant soumettre votre demande</p>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={!simulation}
              className={`w-full px-6 py-3 rounded-lg font-semibold transition-colors ${
                simulation 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {simulation ? '✓ Envoyer la demande' : '⚠️ Simulez d\'abord votre crédit'}
            </button>
          </form>
        </div>
      )}

      {/* Loans List */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-gray-900">Mes demandes de crédit</h2>
        {loans.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 border border-gray-200 text-center">
            <p className="text-gray-500">Aucune demande de crédit</p>
          </div>
        ) : (
          loans.map((loan) => (
            <div
              key={loan.id}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{loanTypes[loan.loan_type]?.icon || '💰'}</span>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {loanTypes[loan.loan_type]?.name || loan.loan_type}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {loan.reference_number} • 
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                        loan.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        loan.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                        loan.status === 'disbursed' ? 'bg-green-100 text-green-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {loan.status === 'pending' ? 'En attente' :
                         loan.status === 'approved' ? 'Approuvé' :
                         loan.status === 'disbursed' ? 'Versé' : 'Refusé'}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Montant {loan.status === 'approved' || loan.status === 'disbursed' ? 'approuvé' : 'demandé'}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {(loan.amount_approved || loan.amount_requested)?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </p>
                </div>
              </div>

              <p className="text-gray-700 mb-4">{loan.description}</p>

              <div className="grid grid-cols-4 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <p className="text-xs text-gray-600">Durée</p>
                  <p className="font-semibold">{loan.duration_months} mois</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Taux d'intérêt</p>
                  <p className="font-semibold text-blue-600">{loan.interest_rate}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Mensualité</p>
                  <p className="font-semibold">
                    {loan.monthly_payment ? `${loan.monthly_payment.toFixed(2)} €` : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Date demande</p>
                  <p className="font-semibold text-sm">
                    {new Date(loan.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>

              {loan.admin_notes && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm font-semibold text-red-900">Note de la banque :</p>
                  <p className="text-sm text-red-700">{loan.admin_notes}</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Loans;
